import {CalendarOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, PlusOutlined, ReloadOutlined, SearchOutlined} from '@ant-design/icons';
import {Button, Card, Empty, Input, Popconfirm, Space, Tag, Tooltip, message} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {TableData} from '../../../../CommonComponents/TableData';
import {RQDeletePrescriptionAPI, RQMedicineLookupAllAPI, RQPrescriptionAddAPI, RQPrescriptionLookupByPatientAPI, RQPrescriptionUpdateAPI, RqUserLookupAllRole} from '../../../../ReactQuery/apis';
import {RQFollowUpLookupByPatientAPI} from '../../../../ReactQuery/apis/rqFollowUpAPI';
import {selectAuth} from '../../../../Redux/selectors';

import DownloadPrescription from './DownloadPrescription/DownloadPrescription';
import {FollowUpDetails} from './FollowUpScreen/FollowUpList';
import {PrescriptionDetailsDrawer} from './PrescriptionDetailsDrawer';
import PrescriptionForm, {getPersonLabel} from './PrescriptionForm/PrescriptionForm';

// ₹ currency formatter for table display
const fmtCurrency = v => (v || v === 0) ? `₹${Number(v).toLocaleString('en-IN')}` : '—';

// ─── Prescription Section (scoped to this patient) ─────────────────────────────
const PrescriptionScreeen = ({patient, patientId, token}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [detailsDrawer, setDetailsDrawer] = useState({open: false, prescription: null});
  const [followUpDrawer, setFollowUpDrawer] = useState({open: false, prescription: null});
  const [previewPrescription, setPreviewPrescription] = useState(null);
  const [pendingRowId, setPendingRowId] = useState(null);
  const [searchText, setSearchText] = useState('');

  const [form] = require('antd').Form.useForm();

  const {data: prescriptions = [], isFetching: loading, refetch} = RQPrescriptionLookupByPatientAPI(patientId, token, {enabled: !!token && !!patientId});
  const patientsQuery = RqUserLookupAllRole('PATIENT', token, {enabled: !!token});
  const doctorsQuery = RqUserLookupAllRole('DOCTOR', token, {
    enabled: !!token,
  });

  const therapistsQuery = RqUserLookupAllRole('THERAPIST', token, {
    enabled: !!token,
  });
  const medicineNamesQuery = RQMedicineLookupAllAPI(token, {enabled: !!token});
  const {data: followUps = []} = RQFollowUpLookupByPatientAPI(patientId, token, {enabled: !!token && !!patientId});

  // Map each prescription's follow up (added against prescriptionId) for quick lookup in the table
  const followUpByPrescriptionId = useMemo(
    () => followUps.reduce((acc, f) => ({...acc, [f.prescriptionId]: f}), {}),
    [followUps],
  );

  const patientOptions = patientsQuery.data ?? [];
  const doctorOptions = [
    ...(doctorsQuery.data ?? []).map(d => ({...d, role: 'Doctor'})),
    ...(therapistsQuery.data ?? []).map(t => ({...t, role: 'Therapist'})),
  ];
  const medicineNameOptions = medicineNamesQuery.data ?? [];

  const consultantName = getPersonLabel(useSelector(selectAuth)) || 'Doctor';
  const patientLabelById = useMemo(() => ({[patientId]: patient?.fullName}), [patientId, patient]);

  const filteredPrescriptions = useMemo(() => {
    let list = [...prescriptions].sort((a, b) => new Date(b.createdDate ?? 0) - new Date(a.createdDate ?? 0));

    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();

      list = list.filter(p => p.doctorNotes?.toLowerCase().includes(q)
        || (p.medicines || []).some(m => m.medicineName?.toLowerCase().includes(q)));
    }

    return list;
  }, [prescriptions, searchText]);

  const addPrescription = RQPrescriptionAddAPI({
    onSuccess: () => {
      message.success('Prescription added successfully');
      refetch();
      closeFormModal();
    },
    onError: err => message.error(err?.response?.data?.message || err?.message || 'Failed to add prescription'),
  });

  const updatePrescription = RQPrescriptionUpdateAPI({
    onSuccess: () => {
      message.success('Prescription updated successfully');
      refetch();
      closeFormModal();
    },
    onError: err => message.error(err?.response?.data?.message || err?.message || 'Failed to update prescription'),
    onSettled: () => setPendingRowId(null),
  });

  const deletePrescription = RQDeletePrescriptionAPI({
    onSuccess: () => {
      message.success('Prescription deleted successfully');
      refetch();
    },
    onError: err => message.error(err?.response?.data?.message || err?.message || 'Failed to delete prescription'),
    onSettled: () => setPendingRowId(null),
  });

  const openAddModal = () => {
    setEditingPrescription(null);
    setIsFormOpen(true);
    // Pre-fill and lock the patient for this context
    setTimeout(() => form.setFieldsValue({patientId}), 0);
  };

  const openEditModal = record => {
    if (pendingRowId === record.prescriptionId) {return;}
    setEditingPrescription(record);
    setIsFormOpen(true);
  };

  const closeFormModal = () => {
    setIsFormOpen(false);
    setEditingPrescription(null);
    form.resetFields();
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then(values => {
        const payload = {
          ...values,
          patientId,
          createdDate: values.createdDate ? values.createdDate.format('YYYY-MM-DD') : null,
          // totalMedicinePrice is already computed & kept in sync inside PrescriptionForm,
          // but we recompute it here too as a safety net in case any field was skipped.
          totalMedicinePrice: values.totalMedicinePrice
            ?? (values.medicines || []).reduce(
              (sum, m) => sum + ((Number(m.quantity) || 1) * (Number(m.sellingPrice) || 0)),
              0,
            ),
        };

        if (editingPrescription) {
          setPendingRowId(editingPrescription.prescriptionId);
          updatePrescription.mutate({id: editingPrescription.prescriptionId, formData: payload, token});
        } else {
          if (!token) {
            message.error('You are not logged in. Please log in again.');

            return;
          }
          addPrescription.mutate({formData: payload, token});
        }
      })
      .catch(() => message.error('Please fill all required fields correctly.'));
  };

  const handleDelete = id => {
    setPendingRowId(id);
    deletePrescription.mutate({id, token});
  };

  const openDetailsDrawer = prescription => setDetailsDrawer({open: true, prescription});
  const closeDetailsDrawer = () => setDetailsDrawer({open: false, prescription: null});

  const openFollowUpDrawer = prescription => setFollowUpDrawer({open: true, prescription});
  const closeFollowUpDrawer = () => setFollowUpDrawer({open: false, prescription: null});

  const latestDate = filteredPrescriptions[0]?.createdDate;

  const columns = [
    {title: 'Sr. No.', key: 'srno', width: 70, render: (_, __, index) => index + 1},
    {title: 'Created Date', dataIndex: 'createdDate'},
    {title: 'Medicines', dataIndex: 'medicines', render: meds => <Tag color={'geekblue'}>{meds?.length ?? 0} item(s)</Tag>},
    {
      title: 'Total Price',
      dataIndex: 'totalMedicinePrice',
      width: 120,
      render: value => <Tag color={'purple'}>{fmtCurrency(value)}</Tag>,
    },
    {title: 'Panchakarma', dataIndex: 'panchakarmaAdvice', render: items => <Tag color={'gold'}>{items?.length ?? 0} item(s)</Tag>},
    {
      title: 'Follow Up Days',
      key: 'followUpDays',
      width: 130,
      render: (_, record) => {
        const followUp = followUpByPrescriptionId[record.prescriptionId];

        return followUp ? (
          <Tag color={'blue'}>
            {followUp.followUpDays} Day{followUp.followUpDays > 1 ? 's' : ''}
          </Tag>
        ) : (
          '—'
        );
      },
    },
    {
      title: 'Next Visit',
      key: 'nextVisit',
      width: 140,
      render: (_, record) => {
        const followUp = followUpByPrescriptionId[record.prescriptionId];

        return followUp?.nextVisit || '—';
      },
    },
    {title: 'Diet Advice', dataIndex: 'dietAdvice', render: items => <Tag color={'green'}>{items?.length ?? 0} item(s)</Tag>},
    {title: 'Doctor Notes', dataIndex: 'doctorNotes', ellipsis: true},
    {
      title: 'Actions',
      key: 'actions',
      width: 190,
      render: (_, record) => {
        const rowBusy = pendingRowId === record.prescriptionId;
        const isDeletingThisRow = rowBusy && deletePrescription.isPending;

        return (
          <Space size={4}>
            <Tooltip title={'View'}>
              <Button size={'small'} icon={<EyeOutlined />} onClick={() => openDetailsDrawer(record)} className={'text-primary-600 border-primary-200 hover:border-primary-400'} />
            </Tooltip>
            <Tooltip title={'Follow Up'}>
              <Button size={'small'} icon={<CalendarOutlined />} onClick={() => openFollowUpDrawer(record)} disabled={rowBusy} className={'text-primary-600 border-primary-200 hover:border-primary-400'} />
            </Tooltip>
            <Tooltip title={'Download'}>
              <Button size={'small'} icon={<DownloadOutlined />} onClick={() => setPreviewPrescription(record)} disabled={rowBusy} className={'text-primary-600 border-primary-200 hover:border-primary-400'} />
            </Tooltip>
            <Tooltip title={'Edit'}>
              <Button size={'small'} icon={<EditOutlined />} onClick={() => openEditModal(record)} disabled={rowBusy} className={'text-primary-600 border-primary-200 hover:border-primary-400'} />
            </Tooltip>
            <Tooltip title={'Delete'}>
              <Popconfirm
                title={'Delete this prescription?'}
                description={'This action cannot be undone.'}
                onConfirm={() => handleDelete(record.prescriptionId)}
                okButtonProps={{danger: true, loading: isDeletingThisRow}}
                okText={'Delete'}
                disabled={rowBusy}
              >
                <Button size={'small'} icon={<DeleteOutlined />} danger loading={isDeletingThisRow} disabled={rowBusy && !isDeletingThisRow} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div className={'mb-5 flex flex-wrap gap-3 items-center justify-between'}>
        <div className={'flex flex-wrap gap-3 items-center'}>
          <Input
            prefix={<SearchOutlined className={'text-gray-400'} />}
            placeholder={'Search by medicine or notes…'}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            style={{width: 280}}
          />
          {searchText.trim() && <Tag color={'blue'} className={'text-xs'}>{filteredPrescriptions.length} result{filteredPrescriptions.length !== 1 ? 's' : ''}</Tag>}
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={loading} className={'btn-secondary'}>Refresh</Button>
          <Button type={'primary'} icon={<PlusOutlined />} onClick={openAddModal} className={'btn-primary'}>Add Prescription</Button>
        </Space>
      </div>

      <Card className={'rounded-lg border border-primary-100 shadow-sm'} bodyStyle={{padding: 0}}>
        <TableData
          dataSource={filteredPrescriptions}
          columns={columns}
          rowKey={'prescriptionId'}
          loading={loading}
          size={'middle'}
          scroll={{x: 1200}}
          rowClassName={'hover:bg-primary-50 transition-colors'}
          locale={{emptyText: <Empty description={'No prescriptions issued for this patient yet'} />}}
        />
      </Card>

      <PrescriptionForm
        open={isFormOpen}
        editingPrescription={editingPrescription}
        patientOptions={patientOptions}
        patientsLoading={patientsQuery.isFetching}
        doctorOptions={doctorOptions}
        doctorsLoading={doctorsQuery.isFetching}
        medicineNameOptions={medicineNameOptions}
        medicinesLoading={medicineNamesQuery.isFetching}
        confirmLoading={addPrescription.isPending || updatePrescription.isPending}
        onCancel={closeFormModal}
        onSubmit={handleFormSubmit}
        form={form}
      />

      <DownloadPrescription
        open={!!previewPrescription}
        onClose={() => setPreviewPrescription(null)}
        prescription={previewPrescription}
        patient={patient ? {name: patient.fullName, age: patient.age, gender: patient.gender} : null}
        consultantName={consultantName}
        followUp={previewPrescription ? followUpByPrescriptionId[previewPrescription.prescriptionId] : null}
      />

      <PrescriptionDetailsDrawer
        open={detailsDrawer.open}
        prescription={detailsDrawer.prescription}
        patientLabelById={patientLabelById}
        onClose={closeDetailsDrawer}
      />

      {/* ─── Follow Up — shown per prescription via a details drawer, not a separate table ─── */}
      <FollowUpDetails
        open={followUpDrawer.open}
        prescription={followUpDrawer.prescription}
        patientId={patientId}
        token={token}
        onClose={closeFollowUpDrawer}
      />
    </div>
  );
};

PrescriptionScreeen.propTypes = {
  patient: PropTypes.object,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  token: PropTypes.string,
};

export default PrescriptionScreeen;
